import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import StarsIcon from '@mui/icons-material/Stars';
import { Box, Container, Typography, Button, Chip, Grid, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PageContainer } from '@/components/layout';
import { AnimatedSection } from '@/components/ui';

const coreStack = ['Java', 'Spring Boot', 'GCP', 'Microservices', 'React'];

export const HomePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [cardZIndices, setCardZIndices] = useState({
    speed: 3,
    experience: 1,
    codeWindow: 2,
  });
  const [maxZIndex, setMaxZIndex] = useState(3);

  const bringToFront = (cardName: 'speed' | 'experience' | 'codeWindow') => {
    const newMaxZIndex = maxZIndex + 1;
    setMaxZIndex(newMaxZIndex);
    setCardZIndices((prev) => ({
      ...prev,
      [cardName]: newMaxZIndex,
    }));
  };

  return (
    <PageContainer>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 80px)' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <AnimatedSection>
                {/* Status Badge */}
                <Chip
                  label={t('home.badge')}
                  icon={
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'secondary.main',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.5 },
                        },
                      }}
                    />
                  }
                  sx={{
                    mb: 4,
                    px: 2,
                    py: 0.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    backdropFilter: 'blur(8px)',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    },
                  }}
                />

                {/* Main Heading */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 3,
                  }}
                >
                  {t('home.greeting')} <br />
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(to right, #8b5cf6, #06b6d4, #3b82f6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {t('home.name')}
                  </Box>
                </Typography>

                {/* Description */}
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.secondary',
                    maxWidth: '42rem',
                    lineHeight: 1.7,
                    mb: 5,
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                  }}
                >
                  {t('home.title')}{' '}
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {t('home.titleHighlight1')}
                  </Box>{' '}
                  {t('home.titleMiddle')}{' '}
                  <Box component="span" sx={{ color: 'secondary.main' }}>
                    {t('home.titleHighlight2')}
                  </Box>{' '}
                  {t('home.titleEnd')}
                </Typography>

                {/* CTA Buttons */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 6 }}>
                  <Button
                    component={Link}
                    to="/projects"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.125rem',
                    }}
                  >
                    {t('home.viewWork')}
                  </Button>
                  <Button
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    size="large"
                    endIcon={<ChatBubbleOutlineIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.125rem',
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {t('home.contactMe')}
                  </Button>
                </Box>

                {/* Core Stack */}
                <Box sx={{ pt: 4, borderTop: '1px solid', borderColor: theme.palette.divider }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                      display: 'block',
                      fontWeight: 600,
                    }}
                  >
                    {t('home.coreStack')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {coreStack.map((tech) => (
                      <Chip
                        key={tech}
                        label={tech}
                        sx={{
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: theme.palette.divider,
                          fontFamily: 'monospace',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </AnimatedSection>
            </Grid>

            {/* Code Editor Window */}
            <Grid size={{ xs: 12, lg: 5 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
              <AnimatedSection delay={200}>
                <Box sx={{ position: 'relative' }}>
                  {/* Main Code Window */}
                  <Box
                    onClick={() => bringToFront('codeWindow')}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      borderRadius: 3,
                      p: 3,
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                          : '0 20px 60px rgba(0, 0, 0, 0.1)',
                      transform: 'rotate(2deg)',
                      transition: 'transform 0.7s ease-out',
                      position: 'relative',
                      zIndex: cardZIndices.codeWindow,
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'rotate(0deg)',
                      },
                    }}
                  >
                    {/* Window Controls */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        pb: 2,
                        borderBottom: '1px solid',
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f56' }}
                        />
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }}
                        />
                        <Box
                          sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27c93f' }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          opacity: 0.6,
                        }}
                      >
                        {t('home.codeWindow.filename')}
                      </Typography>
                    </Box>

                    {/* Code Content */}
                    <Box
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: 2,
                        color: 'text.secondary',
                      }}
                    >
                      <Box>
                        <Box component="span" sx={{ color: 'primary.main' }}>
                          const
                        </Box>{' '}
                        <Box component="span" sx={{ color: '#3b82f6' }}>
                          profile
                        </Box>{' '}
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          =
                        </Box>{' '}
                        <Box component="span" sx={{ color: '#fbbf24' }}>
                          {'{'}
                        </Box>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          name:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'secondary.main' }}>
                          &quot;{t('home.codeWindow.name')}&quot;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ,
                        </Box>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          role:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'secondary.main' }}>
                          &quot;{t('home.codeWindow.role')}&quot;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ,
                        </Box>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          traits:
                        </Box>{' '}
                        <Box component="span" sx={{ color: '#fbbf24' }}>
                          [
                        </Box>
                      </Box>
                      <Box sx={{ pl: 6 }}>
                        <Box component="span" sx={{ color: 'secondary.main' }}>
                          &quot;{t('home.codeWindow.traits.scalable')}&quot;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ,
                        </Box>
                      </Box>
                      <Box sx={{ pl: 6 }}>
                        <Box component="span" sx={{ color: 'secondary.main' }}>
                          &quot;{t('home.codeWindow.traits.secure')}&quot;
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ,
                        </Box>
                      </Box>
                      <Box sx={{ pl: 6 }}>
                        <Box component="span" sx={{ color: 'secondary.main' }}>
                          &quot;{t('home.codeWindow.traits.innovative')}&quot;
                        </Box>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Box component="span" sx={{ color: '#fbbf24' }}>
                          ]
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ,
                        </Box>
                      </Box>
                      <Box sx={{ pl: 3 }}>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          status:
                        </Box>{' '}
                        <Box component="span" sx={{ color: 'primary.main' }}>
                          &quot;{t('home.codeWindow.status')}&quot;
                        </Box>
                      </Box>
                      <Box>
                        <Box component="span" sx={{ color: '#fbbf24' }}>
                          {'}'}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.6)'
                                : 'rgba(0, 0, 0, 0.6)',
                          }}
                        >
                          ;
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Speed Badge - Bottom Left */}
                  <Box
                    onClick={() => bringToFront('speed')}
                    sx={{
                      position: 'absolute',
                      bottom: -24,
                      left: -24,
                      bgcolor: 'background.paper',
                      p: 2,
                      borderRadius: 2,
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                          : '0 10px 40px rgba(0, 0, 0, 0.1)',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      animation: 'bounce 2s infinite 0.7s',
                      zIndex: cardZIndices.speed,
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'rgba(59, 130, 246, 0.1)',
                          borderRadius: 1.5,
                          color: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BoltIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            letterSpacing: '0.1em',
                          }}
                        >
                          {t('home.badges.speed')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}
                        >
                          {t('home.badges.speedValue')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Experience Badge - Top Right */}
                  <Box
                    onClick={() => bringToFront('experience')}
                    sx={{
                      position: 'absolute',
                      top: -58,
                      right: -24,
                      bgcolor: 'background.paper',
                      p: 2,
                      borderRadius: 2,
                      boxShadow:
                        theme.palette.mode === 'dark'
                          ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                          : '0 10px 40px rgba(0, 0, 0, 0.1)',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      animation: 'bounce 2s infinite',
                      '@keyframes bounce': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-10px)' },
                      },
                      zIndex: cardZIndices.experience,
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: 'rgba(139, 92, 246, 0.1)',
                          borderRadius: 1.5,
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <StarsIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            letterSpacing: '0.1em',
                          }}
                        >
                          {t('home.badges.experience')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}
                        >
                          {t('home.badges.experienceValue')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AnimatedSection>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </PageContainer>
  );
};
